import { SerializedStyles, css } from '@emotion/react';
import styled from '@emotion/styled';
import { EmotionTheme } from 'src/domains/layouts/config/emotionTheme';
import { DefaultComponentVariantType } from 'src/domains/layouts/config/features/types';
import { ChevronIcon } from 'src/domains/layouts/shared/assets/icons/ChevronIcon';
import { withConfig } from 'src/withConfig';

interface RaceBoxPropsType {
    variant: DefaultComponentVariantType | null;
}

export const setRaceBox = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css``;
    }
    return css`
        border-style: solid;
        border-color: ${theme.star.heroWidget.borderColor};
        border-width: 0 0 1px 1px;
    `;
};

export const RaceBox = withConfig(theme => styled('section', { label: 'RaceBox' })<RaceBoxPropsType>`
    ${({ variant }): SerializedStyles => setRaceBox(variant, theme)}
    position: relative;
`);

interface ListWrapperPropsType {
    variant: DefaultComponentVariantType | null;
    autoHeightItem?: boolean;
}

export const setListWrapper = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            gap: ${theme.star.gap.small};
            @media ${theme.star.mediaQuery.desktop} {
                grid-template-columns: repeat(2, 1fr);
            }
            li {
                background-color: ${theme.star.heroWidget.bgColorTertiary};
                border-radius: ${theme.star.radius.medium};
            }
        `;
    }
    return css`
        background-color: ${theme.star.heroWidget.bgColorQuaternary};
        @media ${theme.star.mediaQuery.desktop} {
            grid-template-columns: repeat(2, 50%);
        }
        li {
            border-bottom: 1px solid ${theme.star.heroWidget.borderColor};
        }
        &:last-child {
            border-bottom: 1px solid ${theme.star.heroWidget.borderColor};
        }
        @media ${theme.star.mediaQuery.desktop} {
            flex: 0 0 50%;
        }
        .selection {
            border-right: 1px solid ${theme.star.heroWidget.borderColor};
        }
    `;
};

export const ListWrapper = withConfig(theme => styled('ul', { label: 'ListWrapper' })<ListWrapperPropsType>`
    ${({ variant }): SerializedStyles => setListWrapper(variant, theme)}
    display: grid;
    li {
        display: flex;
        min-height: 40px;
        height: ${({ autoHeightItem }): string => autoHeightItem === true ? 'auto' : '40px'};
        flex: 0 0 100%;
    }
    .selection {
        height: 100%;
    }
`);

export const SpecialEventsHeaderMaxbetText = withConfig(theme => styled('div', { label: 'SpecialEventsHeaderMaxbetText' })`
    font-size: 11px;
    font-weight: normal;
    color: ${theme.star.heroWidget.txtColor };
    line-height: 1;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-end;
    padding: 10px;
`);

export const EventName = withConfig(theme => styled('span', { label: 'EventName' })`
    color: ${theme.star.heroWidget.txtColor };
    font-weight: ${theme.star.fontWeight.bold };
    position: relative;
    text-overflow: ellipsis;
    text-transform: uppercase;
    font-size: ${theme.star.fontSize.regular};
    line-height: 1.25;
    @media ${theme.star.mediaQuery.tablet} {
        font-size: ${theme.star.fontSize.big};
    }
`);

export const EventInfo = withConfig(theme => styled('span', { label: 'EventInfo' })`
    display: block;
    color: ${theme.star.heroWidget.txtColor};
    font-size: ${theme.star.fontSize.xSmall };
    position: relative;
    text-overflow: ellipsis;
    line-height: 1.4;
    @media ${theme.star.mediaQuery.tablet} {
        font-size: ${theme.star.fontSize.xRegular };
        line-height: 1.43;
    }
`);


interface ShowMoreWrapperPropsType {
    variant: DefaultComponentVariantType | null;
}

export const setShowMoreWrapper = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            border-radius: ${theme.star.radius.medium};
            margin-top: 4px;
        `;
    }
    return css``;
};

export const ShowMoreWrapper = withConfig(theme => styled('div', { label: 'ShowMoreWrapper' })<ShowMoreWrapperPropsType>`
    ${({ variant }): SerializedStyles => setShowMoreWrapper(variant, theme)}
    align-items: center;
    background-color: ${theme.star.heroWidget.bgColorTertiary};
    color: ${theme.star.heroWidget.txtColorSecondary};
    cursor: pointer;
    display: flex;
    flex-flow: row nowrap;
    font-size: ${theme.star.fontSize.small};
    font-weight: ${theme.star.fontWeight.bold};
    height: 40px;
    line-height: 1.3334;
    padding: 12px;
    text-transform: capitalize;
`);

export const GoToChevronIcon = styled(ChevronIcon, { label: 'GoToChevronIcon' })`
    width: 12px;
    fill: currentcolor;
    margin-left: 4px;
`;

export interface PromoBannersType {
    mobile: string | null;
    tablet: string | null;
    desktop: string | null;
    bgColor?: string | null;
}

export const heroImageStyles = (banners: PromoBannersType, theme: EmotionTheme): SerializedStyles => css`
    background-position: center;
    background-repeat: no-repeat;
    background-size: auto 96px;
    height: 96px;
    ${banners.mobile === null ? '' : `background-image: url(${banners.mobile})`};
    @media ${theme.star.mediaQuery.tablet} {
        ${banners.tablet === null ? '' : `background-image: url(${banners.tablet})`};
    }
    @media ${theme.star.mediaQuery.largeDesktop}{
        ${banners.desktop === null ? '' : `background-image: url(${banners.desktop})`};
    }
`;

export interface BannersType {
    banners: PromoBannersType;
    withMargin?: boolean;
    iaContent?: boolean;
}

export const WidgetBanner = withConfig(theme => styled('div', { label: 'WidgetBanner' })<BannersType>`
    background-color: ${({ banners }): string => banners.bgColor ?? theme.star.heroWidget.bgColor};
    ${({ banners }): SerializedStyles => heroImageStyles(banners, theme) };
    padding: 0 10px;
    cursor: ${({ iaContent }): string => iaContent === true ? 'pointer' :'default' };
`);

export const EventWrapper = styled('div', { label: 'EventWrapper' })`
    max-width: 270px;
    @media screen and (min-width: 420px) {
        max-width: none;
    }
`;

export const EventEachWay = withConfig(theme => styled('div', { label: 'EventEachWay' })`
    font-size: ${theme.star.fontSize.xSmall};
    line-height: 1;
    color: ${theme.star.heroWidget.txtColor};
    display: flex;

    @media ${theme.star.mediaQuery.desktop} {
        font-size: ${theme.star.fontSize.small};
        line-height: 1;
    }
`);


interface MarketGroupSectionPropsType {
    variant: DefaultComponentVariantType | null;
}

export const setMarketGroupSection = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css``;
    }
    return css`
        border-color: ${theme.star.heroWidget.borderColor};
        border-style: solid;
        border-width: 0 1px;
    `;
};


export const MarketGroupSection = withConfig(theme => styled('section', { label: 'MarketGroupSection' })<MarketGroupSectionPropsType>`
    ${({ variant }): SerializedStyles => setMarketGroupSection(variant, theme)}
`);

export const setHeroWidgetWrapperWrapper = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            padding: ${theme.star.padding.medium};
            overflow: hidden;
            background: ${theme.star.heroWidget.bgColorSecondary};
            @media ${theme.star.mediaQuery.tablet} {
                border-radius: ${theme.star.radius.medium};
                margin: 0 8px;
            }
        `;
    }
    return css``;
};

interface HeroWidgetWrapperPropsType {
    variant: DefaultComponentVariantType | null;
}

export const HeroWidgetWrapper = withConfig(theme => styled('div', { label: 'HeroWidgetWrapper' })<HeroWidgetWrapperPropsType>`
    ${({ variant }): SerializedStyles => setHeroWidgetWrapperWrapper(variant, theme)}
`);

export const ShowMoreArrowWrapper = styled(ChevronIcon, { label: 'ShowMoreArrowWrapper' })`
    fill: currentcolor;
    margin-left: 4px;
    position: relative;
    width: 12px;
`;
